import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AsyncTaskUI, useAsyncTaskUIContext } from "@/modern/components/async-tasks/async-task-ui";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import {
  isAsyncTaskType,
  matchBoardSiteAsyncTask,
} from "@/modern/components/async-tasks/utils";
import { getApiErrorMessage } from "@/integrations";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { showToast } from "@/other/utils/utils";
import { MarketingPage } from "@/bryntum-lib/models/MarketingPage";
import { Site } from "@/bryntum-lib/models/Site";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useSite } from "../hooks/useSite";
import { findSiteForBoard, mergeSitePublishFromApi } from "../utils";

export interface SitePublishBridgeValue {
  setManualPublish: (value: boolean) => void;
  setPublishLiveTaskId: (taskId: string | null) => void;
  publishSuccessNonce: number;
  isPublishing: boolean;
  isRepublishing: boolean;
  isDeploying: boolean;
}

const SitePublishBridgeContext = createContext<SitePublishBridgeValue | null>(null);

export function useSitePublishBridge(): SitePublishBridgeValue {
  const ctx = useContext(SitePublishBridgeContext);
  if (!ctx) {
    throw new Error("useSitePublishBridge must be used within MarketingSiteAsyncShell");
  }
  return ctx;
}

function isPublishLiveTask(task: AsyncTask, publishLiveTaskId: string | null): boolean {
  return publishLiveTaskId != null && String(task.id) === publishLiveTaskId;
}

function SitePublishBridgeInner({
  children,
  publishSuccessNonce,
  isManualPublish,
  setManualPublish,
  publishLiveTaskId,
  setPublishLiveTaskId,
}: {
  children: ReactNode;
  publishSuccessNonce: number;
  isManualPublish: boolean;
  setManualPublish: (value: boolean) => void;
  publishLiveTaskId: string | null;
  setPublishLiveTaskId: (taskId: string | null) => void;
}) {
  const { taskMatching } = useAsyncTaskUIContext();
  const isPublishing = taskMatching(
    (task) =>
      isAsyncTaskType(task, ASYNC_TASK_TYPES.SITE_PUBLISH) &&
      !isPublishLiveTask(task, publishLiveTaskId),
  );
  const isDeploying = taskMatching(
    (task) =>
      isAsyncTaskType(task, ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY) ||
      (isAsyncTaskType(task, ASYNC_TASK_TYPES.SITE_PUBLISH) &&
        isPublishLiveTask(task, publishLiveTaskId)),
  );
  const isRepublishing = isPublishing && !isManualPublish;

  const value = useMemo(
    () => ({
      setManualPublish,
      setPublishLiveTaskId,
      publishSuccessNonce,
      isPublishing,
      isRepublishing,
      isDeploying,
    }),
    [
      setManualPublish,
      setPublishLiveTaskId,
      publishSuccessNonce,
      isPublishing,
      isRepublishing,
      isDeploying,
    ],
  );

  return (
    <SitePublishBridgeContext.Provider value={value}>
      {children}
    </SitePublishBridgeContext.Provider>
  );
}

/** Shared AsyncTaskUI root for site publish/deploy — used by Marketing Pages and Site views. */
export function MarketingSiteAsyncShell({ children }: { children: ReactNode }) {
  const { project, selectedBoard } = useSwivuBryntumContext();
  const { fetchResult } = useAsyncTaskContext();
  const boardId = (selectedBoard?.record as { id?: number } | undefined)?.id;
  const [isManualPublish, setManualPublish] = useState(false);
  const [publishSuccessNonce, setPublishSuccessNonce] = useState(0);
  const [publishLiveTaskId, setPublishLiveTaskId] = useState<string | null>(null);

  const taskFilter = useCallback(
    (task: AsyncTask) => boardId != null && matchBoardSiteAsyncTask(task, boardId),
    [boardId],
  );

  const mergePublishResult = useCallback(
    (data: unknown) => {
      if (!project || boardId == null) return;
      const store = project.getCrudStore(STORE_KEYS.sitesStore);
      const site = findSiteForBoard(store, boardId);
      if (site) mergeSitePublishFromApi(site, data);
    },
    [project, boardId],
  );

  const effects = useMemo(
    () => ({
      onCompleted: (task: AsyncTask) => {
        void (async () => {
          const isLiveTask =
            isAsyncTaskType(task, ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY) ||
            (isAsyncTaskType(task, ASYNC_TASK_TYPES.SITE_PUBLISH) &&
              isPublishLiveTask(task, publishLiveTaskId));
          const isPreviewPublish =
            isAsyncTaskType(task, ASYNC_TASK_TYPES.SITE_PUBLISH) && !isLiveTask;

          if (isPreviewPublish) {
            setManualPublish(false);
            setPublishSuccessNonce((n) => n + 1);
            showToast({
              msg: "Site published successfully.",
              type: "success",
              duration: 4000,
            });
          }

          if (isLiveTask) {
            setPublishLiveTaskId(null);
            showToast({
              msg: "Site published live successfully.",
              type: "success",
              duration: 4000,
            });
          }

          try {
            const data = await fetchResult(task.id);
            mergePublishResult(data);
          } catch (error) {
            if (isPreviewPublish) {
              showToast({
                msg: getApiErrorMessage(
                  error,
                  "Site publish finished but the result could not be loaded.",
                ),
                type: "error",
                duration: 5000,
              });
            } else if (isLiveTask) {
              showToast({
                msg: getApiErrorMessage(
                  error,
                  "Publish live finished but the result could not be loaded.",
                ),
                type: "error",
                duration: 5000,
              });
            }
          }
        })();
      },
      onFailed: (task: AsyncTask) => {
        void (async () => {
          const isLiveTask =
            isAsyncTaskType(task, ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY) ||
            (isAsyncTaskType(task, ASYNC_TASK_TYPES.SITE_PUBLISH) &&
              isPublishLiveTask(task, publishLiveTaskId));
          const isPreviewPublish =
            isAsyncTaskType(task, ASYNC_TASK_TYPES.SITE_PUBLISH) && !isLiveTask;
          const cancelled = task.status === "Cancelled";

          if (isPreviewPublish) {
            setManualPublish(false);
          }

          if (isLiveTask) {
            setPublishLiveTaskId(null);
          }

          const fallback = isLiveTask
            ? cancelled
              ? "Publish live cancelled."
              : "Publish live failed."
            : cancelled
              ? "Site publish cancelled."
              : "Site publish failed.";

          if (cancelled) {
            showToast({ msg: fallback, type: "error", duration: 4000 });
            return;
          }

          try {
            const data = await fetchResult(task.id);
            mergePublishResult(data);
            showToast({
              msg: getApiErrorMessage(data, fallback),
              type: "error",
              duration: 5000,
            });
          } catch (error) {
            showToast({
              msg: getApiErrorMessage(error, fallback),
              type: "error",
              duration: 5000,
            });
          }
        })();
      },
    }),
    [fetchResult, mergePublishResult, publishLiveTaskId],
  );

  if (boardId == null) {
    return null;
  }

  return (
    <AsyncTaskUI filter={taskFilter} effects={effects}>
      <SitePublishBridgeInner
        publishSuccessNonce={publishSuccessNonce}
        isManualPublish={isManualPublish}
        setManualPublish={setManualPublish}
        publishLiveTaskId={publishLiveTaskId}
        setPublishLiveTaskId={setPublishLiveTaskId}
      >
        {children}
      </SitePublishBridgeInner>
    </AsyncTaskUI>
  );
}

interface MarketingPagesContextValue {
  selectedPage: MarketingPage | null;
  toggleSelectedPage: (page: MarketingPage) => void;
  clearSelectedPage: () => void;
  siteRecord: Site | null;
  ensureSiteRecord: () => Site | null;
  isPublishing: boolean;
  isRepublishing: boolean;
}

const MarketingPagesContext = createContext<MarketingPagesContextValue | undefined>(undefined);

export function MarketingPagesProvider({ children }: { children: ReactNode }) {
  const { project } = useSwivuBryntumContext();
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const site = useSite();

  const { version: pagesVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingPageStore });

  const pages = useMemo<MarketingPage[]>(() => {
    if (!project) return [];
    const store = project.getCrudStore(STORE_KEYS.marketingPageStore);
    if (!store) return [];
    return store.query(() => true) as unknown as MarketingPage[];
  }, [project, pagesVersion]);

  const selectedPage = useMemo(
    () => pages.find((p) => p.id === selectedPageId) ?? null,
    [pages, selectedPageId],
  );

  const toggleSelectedPage = useCallback((page: MarketingPage) => {
    setSelectedPageId((prev) => (prev === page.id ? null : page.id));
  }, []);

  const clearSelectedPage = useCallback(() => setSelectedPageId(null), []);

  const value = useMemo(
    () => ({
      selectedPage,
      toggleSelectedPage,
      clearSelectedPage,
      siteRecord: site.siteRecord,
      ensureSiteRecord: site.ensureSiteRecord,
      isPublishing: site.isPublishing,
      isRepublishing: site.isRepublishing,
    }),
    [
      selectedPage,
      toggleSelectedPage,
      clearSelectedPage,
      site.siteRecord,
      site.ensureSiteRecord,
      site.isPublishing,
      site.isRepublishing,
    ],
  );

  return (
    <MarketingPagesContext.Provider value={value}>{children}</MarketingPagesContext.Provider>
  );
}

export function useMarketingPagesContext() {
  const ctx = useContext(MarketingPagesContext);
  if (!ctx) {
    throw new Error("useMarketingPagesContext must be used within MarketingPagesProvider");
  }
  return ctx;
}
